/**
 * Identitatea vanzatorului (ADR-003).
 *
 * Firma care gateste e vanzatorul fata de consumator, iar identitatea ei e
 * informatie precontractuala (OUG 34/2014 art. 6): denumire, CUI, sediu,
 * telefon, afisate INAINTE de butonul de confirmare, nu pe pagina de contact.
 *
 * Cand sunt mai multi vanzatori (split), se afiseaza toti si se spune explicit
 * ca vor fi emise mai multe documente fiscale — un client care primeste doua
 * bonuri fara sa fi fost anuntat crede ca a fost taxat de doua ori.
 */

import type { SellerIdentityDto } from "@deliveryapp/shared";

/** Un text de configurare nu devine numar de telefon doar fiindca este in
 * campul `phone`. Evita linkuri precum `tel:necompletatinPOS`, care par
 * apasabile si apoi nu pot suna nicaieri. */
export function isUsableSellerPhone(phone: string | null | undefined): phone is string {
  return typeof phone === "string" && phone.replace(/\D/g, "").length >= 6;
}

export function SellerLine({ seller }: { seller: SellerIdentityDto }) {
  return (
    <div className="text-sm">
      <p className="font-semibold text-ink">{seller.displayName}</p>
      <p className="text-ink-muted">
        {seller.legalName}
        {" · CUI "}
        {seller.taxId}
      </p>
    </div>
  );
}

export function SellerIdentityCard({
  sellers,
  heading = "Vânzător",
  headingPlural = "Vânzători",
  supportPhone,
}: {
  sellers: SellerIdentityDto[];
  heading?: string;
  headingPlural?: string;
  supportPhone?: string;
}) {
  if (sellers.length === 0) {
    // Fara vanzator nu exista comanda. Golul aici nu e o stare de afisaj, e o
    // eroare: se spune, nu se ascunde.
    return (
      <section className="rounded-card border border-danger bg-surface p-4" aria-labelledby="vanzator-lipsa">
        <h2 id="vanzator-lipsa" className="text-sm font-semibold text-danger">
          Vânzătorul nu este stabilit
        </h2>
        <p className="mt-1 text-sm text-ink">
          Comanda nu poate fi confirmată până când nu se stabilește firma care o pregătește.
        </p>
      </section>
    );
  }

  const multiple = sellers.length > 1;

  return (
    <section className="rounded-card border border-line bg-surface p-4" aria-labelledby="vanzator-titlu">
      <h2 id="vanzator-titlu" className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
        {multiple ? `${headingPlural} (${sellers.length})` : heading}
      </h2>

      {multiple ? (
        <p className="mt-2 rounded-control border border-warning px-3 py-2 text-sm text-ink">
          Comanda ta se împarte între {sellers.length} vânzători. Fiecare pregătește partea lui și emite
          propriul bon fiscal, deci vei primi {sellers.length} documente fiscale.
        </p>
      ) : null}

      <ul className="mt-3 flex flex-col gap-4">
        {sellers.map((seller) => (
          <li key={seller.vendorId} className="text-sm">
            <p className="text-base font-semibold text-ink">{seller.displayName}</p>
            <dl className="mt-1 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-ink-muted">
              <dt className="font-medium">Denumire</dt>
              <dd className="text-ink">{seller.legalName}</dd>
              <dt className="font-medium">CUI</dt>
              <dd className="text-ink">{seller.taxId}</dd>
              <dt className="font-medium">Sediu</dt>
              <dd className="text-ink">{seller.registeredAddress}</dd>
              <dt className="font-medium">Telefon</dt>
              <dd className="text-ink">
                {isUsableSellerPhone(seller.phone) ? (
                  <a className="underline" href={`tel:${seller.phone.replace(/\s+/g, "")}`}>
                    {seller.phone}
                  </a>
                ) : isUsableSellerPhone(supportPhone) ? (
                  <>
                    <a className="underline" href={`tel:${supportPhone.replace(/\s+/g, "")}`}>
                      {supportPhone}
                    </a>
                    <span className="text-ink-muted"> (asistența platformei)</span>
                  </>
                ) : (
                  <span className="text-ink-muted">Contact prin platformă</span>
                )}
              </dd>
              {seller.email === null || seller.email === undefined || seller.email.length === 0 ? null : (
                <>
                  <dt className="font-medium">E-mail</dt>
                  <dd className="text-ink">
                    <a className="underline" href={`mailto:${seller.email}`}>
                      {seller.email}
                    </a>
                  </dd>
                </>
              )}
            </dl>
          </li>
        ))}
      </ul>

      <p className="mt-3 text-xs text-ink-muted">
        Contractul de vânzare se încheie cu firma de mai sus, care emite bonul fiscal. Platforma
        intermediază comanda și livrarea.
      </p>
    </section>
  );
}
